import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listReports, resolveReport, updateEventModeration, updateUserModeration } from "@/lib/admin";
import { fmtError } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import ConfirmModal from "@/components/ConfirmModal";
import { Check, Trash2, Pause, Calendar, User } from "lucide-react";
import { toast } from "sonner";

const REASON_LABEL = {
  spam: "Spam",
  fake_event: "Fake event",
  harassment: "Harassment",
  inappropriate: "Inappropriate",
  scam: "Scam",
  other: "Other",
};

const STATUS = [
  { key: "open", label: "Open" },
  { key: "resolved", label: "Resolved" },
  { key: "all", label: "All" },
];

export default function AdminReports() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    listReports({ status })
      .then((r) => setRows(r.rows))
      .catch((e) => toast.error(fmtError(e)))
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (r) => {
    try { await resolveReport(r.id, user.id); toast.success("Resolved."); load(); }
    catch (e) { toast.error(fmtError(e)); }
  };

  const deleteContent = (r) => setConfirm({
    title: r.target_type === "event" ? "Soft-delete this event?" : "Soft-delete this user?",
    body: "Content will disappear from the public app.",
    confirmLabel: "Soft delete",
    danger: true,
    onConfirm: async () => {
      try {
        if (r.target_type === "event") await updateEventModeration(r.target_id, { is_deleted: true });
        else if (r.target_type === "profile") await updateUserModeration(r.target_id, { is_deleted: true });
        await resolveReport(r.id, user.id);
        toast.success("Deleted & resolved.");
        load();
      } catch (e) { toast.error(fmtError(e)); }
    },
  });

  const suspendUser = (r, userId) => setConfirm({
    title: "Suspend this user?",
    body: "They won't be able to post or join events.",
    confirmLabel: "Suspend",
    danger: true,
    onConfirm: async () => {
      try {
        await updateUserModeration(userId, { is_suspended: true });
        await resolveReport(r.id, user.id);
        toast.success("Suspended & resolved.");
        load();
      } catch (e) { toast.error(fmtError(e)); }
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="inline-flex gap-1 p-1 border-[2.5px] border-ink rounded-full bg-paper self-start">
        {STATUS.map((s) => (
          <button key={s.key}
            data-testid={`reports-filter-${s.key}`}
            onClick={() => setStatus(s.key)}
            className={`px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-widest rounded-full transition-colors ${
              status === s.key ? "bg-ink text-paper" : "text-ink/65 hover:text-ink"
            }`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="pop p-0 overflow-hidden">
        {loading ? (
          <p className="px-5 py-10 text-center text-ink/55">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="px-5 py-10 text-center text-ink/55">No reports.</p>
        ) : (
          <div className="divide-y-2 divide-ink/10">
            {rows.map((r) => {
              const targetUserId =
                r.target_type === "profile" ? r.target_id
                : (r.target_type === "event" ? r.target_event?.host_id : null);
              return (
                <div key={r.id} data-testid={`report-row-${r.id}`} className="p-4 flex flex-col gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="bg-lime text-ink border-2 border-ink rounded-full px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-widest">
                      {REASON_LABEL[r.reason] || r.reason}
                    </span>
                    <span className="text-ink/55 text-xs">{new Date(r.created_at).toLocaleString()}</span>
                    {r.status === "resolved" && (
                      <span className="bg-paper text-ink/65 border-2 border-ink/30 rounded-full px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-widest">
                        Resolved
                      </span>
                    )}
                  </div>

                  {r.details && (
                    <p className="text-ink/75 text-sm leading-relaxed bg-cream border-[2px] border-ink/15 rounded-xl p-3">
                      "{r.details}"
                    </p>
                  )}

                  <div className="grid sm:grid-cols-2 gap-3">
                    {/* Reporter */}
                    <div className="border-[2px] border-ink/15 rounded-xl p-3 flex items-center gap-3">
                      <Avatar src={r.reporter?.avatar_url} name={r.reporter?.display_name} size="sm" />
                      <div className="min-w-0 text-xs flex-1">
                        <p className="text-[10px] font-extrabold uppercase tracking-widest text-ink/55">Reporter</p>
                        <p className="font-bold text-ink truncate">
                          {r.reporter?.display_name || "anon"}
                          {r.reporter?.username && <span className="text-ink/40 font-normal ml-1">@{r.reporter.username}</span>}
                        </p>
                      </div>
                    </div>

                    {/* Target */}
                    <div className="border-[2px] border-ink/15 rounded-xl p-3 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full border-[2.5px] border-ink bg-paper flex items-center justify-center">
                        {r.target_type === "event" ? <Calendar size={14} strokeWidth={2.2} /> : <User size={14} strokeWidth={2.2} />}
                      </div>
                      <div className="min-w-0 text-xs flex-1">
                        <p className="text-[10px] font-extrabold uppercase tracking-widest text-ink/55">{r.target_type}</p>
                        {r.target_type === "event" && r.target_event ? (
                          <Link to={`/events/${r.target_id}`} className="font-bold text-ink truncate block hover:text-lime">
                            {r.target_event.title}
                          </Link>
                        ) : r.target_type === "profile" && r.target_profile ? (
                          <Link to={`/u/${r.target_profile.username || r.target_id}`} className="font-bold text-ink truncate block hover:text-lime">
                            @{r.target_profile.username || r.target_profile.display_name}
                          </Link>
                        ) : (
                          <span className="text-ink/55">missing</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {r.status === "open" && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      <button onClick={() => resolve(r)} data-testid={`btn-resolve-${r.id}`}
                        className="inline-flex items-center gap-2 bg-lime text-ink border-[2.5px] border-ink rounded-full px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-widest hover:bg-paper transition-colors">
                        <Check size={12} strokeWidth={2.5} /> Mark resolved
                      </button>
                      <button onClick={() => deleteContent(r)} data-testid={`btn-delete-target-${r.id}`}
                        className="inline-flex items-center gap-2 bg-paper border-[2.5px] border-ink rounded-full px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-widest hover:bg-destructive hover:text-paper transition-colors">
                        <Trash2 size={12} strokeWidth={2.5} /> Delete {r.target_type === "event" ? "event" : "user"}
                      </button>
                      {targetUserId && (
                        <button onClick={() => suspendUser(r, targetUserId)} data-testid={`btn-suspend-target-${r.id}`}
                          className="inline-flex items-center gap-2 bg-paper border-[2.5px] border-ink rounded-full px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-widest hover:bg-ink hover:text-paper transition-colors">
                          <Pause size={12} strokeWidth={2.5} /> Suspend user
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel={confirm?.confirmLabel || "Confirm"}
        danger={confirm?.danger}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => { await confirm?.onConfirm?.(); setConfirm(null); }}
      />
    </div>
  );
}
